import { create } from 'zustand'
import dayjs, { DayjsInput } from 'utils/dayjs'
import { EventService } from '../components/modules/Calendar/services/eventServices'
import { DATE_FORMAT } from '~constants'
import {
  ActiveArtistList,
  ActiveMonthList,
  ArtistProps,
  EventAPIResponse,
  Pagination
} from 'components/modules/Calendar/types'
import { EventsByDate, EventsByMonth, FormattedEvent } from 'components/modules/Calendar/types/Event'
import { EventFormatter } from 'components/modules/Calendar/utils/EventFormatter'
import { MonthProps } from '~types'
import { formatMonth } from '~utils/calendar'
import { EventsMonthParams } from 'types/calendar'

const eventFormatter = new EventFormatter()

interface EventsState {
  startDate: DayjsInput
  endDate: DayjsInput
  selectedDate: DayjsInput
  selectedDateForSingleArtist: DayjsInput | null
  selectedMonthForSingleArtist: MonthProps | null
  selectedMonth: MonthProps
  selectedMonthNav: MonthProps | null
  artists: ArtistProps[]
  loading: boolean
  errors: string | null
  events: EventAPIResponse[]
  eventsByDate: EventsByDate | null
  eventsByMonth: EventsByMonth
  eventsForSingleArtist: EventAPIResponse[] | null
  eventsByDateForSingleArtist: EventsByDate | null
  eventsByMonthForSingleArtist: EventsByMonth | null
  formattedEvents: FormattedEvent[]
  activeMonthList: ActiveMonthList[]
  activeMonthListForSingleArtist: ActiveMonthList[]
  activeArtistList: ActiveArtistList[]
  eventsMonthParams: EventsMonthParams | null
  eventsMonthParamsForSingleArtist: EventsMonthParams | null
  activeShowId?: string | null
  isNavigationClicked?: boolean
  selectedEvent?: {
    events: FormattedEvent[]
    artistDetail: ArtistProps
  } | null
  eventsPagination: Pagination | null

  filteredEventName: string | null
  setFilteredEventName: (name: string | null) => void
  setSelectedMonthNav: (date: DayjsInput) => void
  setSelectedDate: (date: DayjsInput) => void
  setSelectedDateForSingleArtist: (date: DayjsInput) => void
  setSelectedMonthForSingleArtist: (date: DayjsInput) => void
  setStartDate: (date: DayjsInput) => void
  setEndDate: (date: DayjsInput) => void
  setArtists: (artists: ArtistProps[]) => void
  setSelectedEvent: (event: { events: FormattedEvent[]; artistDetail: ArtistProps } | null) => void
  setIsNavigationClicked: (value: boolean) => void
  fetchEvents: (
    startDate: DayjsInput,
    endDate: DayjsInput,
    artists: ArtistProps[],
    selectedArtist: string | null
  ) => Promise<void>
  fetchEventsForSingleArtist: (
    startDate: DayjsInput,
    endDate: DayjsInput,
    artists: ArtistProps[],
    selectedArtist: string | null
  ) => Promise<void>
  setEventsPagination: (value: Pagination) => void
  setActiveShowId: (id: string | null) => void
}

export const useEventsStore = create<EventsState>((set, get) => ({
  startDate: dayjs(),
  endDate: dayjs(),
  selectedDate: dayjs(),
  selectedMonthNav: null,
  selectedDateForSingleArtist: null,
  selectedMonth: formatMonth(dayjs()),
  artists: [],
  loading: true,
  errors: null,
  events: [],
  eventsForSingleArtist: null,
  eventsByDateForSingleArtist: null,
  eventsByMonthForSingleArtist: null,
  activeMonthList: [],
  activeMonthListForSingleArtist: [],
  activeArtistList: [],
  eventsMonthParams: null,
  eventsMonthParamsForSingleArtist: null,
  eventsByDate: null,
  eventsByMonth: {},
  formattedEvents: [],
  selectedEvent: null,
  eventsPagination: null,
  filteredEventName: null,
  setSelectedMonthNav: (date: DayjsInput) => set({ selectedMonthNav: formatMonth(date) }),
  setIsNavigationClicked: (value: boolean) => set({ isNavigationClicked: value }),
  selectedMonthForSingleArtist: null,

  // Setters for various states
  setStartDate: (date: DayjsInput) => set({ startDate: dayjs(date as string) }),

  setEventsPagination: (value: Pagination) => set({ eventsPagination: value }),

  setSelectedMonthForSingleArtist: (date: DayjsInput) => set({ selectedMonthForSingleArtist: formatMonth(date) }),

  setSelectedDateForSingleArtist: (date: DayjsInput) =>
    set({
      selectedDateForSingleArtist: dayjs(date as string),
      selectedMonthForSingleArtist: formatMonth(date)
    }),

  setSelectedDate: (date: DayjsInput) =>
    set({
      selectedDate: dayjs(date as string),
      selectedMonth: formatMonth(date)
    }),

  setEndDate: (date: DayjsInput) => set({ endDate: dayjs(date as string) }),

  setArtists: (artists: ArtistProps[]) => set({ artists }),

  setSelectedEvent: event => set({ selectedEvent: event }),

  setActiveShowId: (id: string | null) => set({ activeShowId: id }),

  setFilteredEventName: (name: string | null) => set({ filteredEventName: name }),

  // Function to fetch events from the API
  fetchEvents: async (startDate, endDate, artists, selectedArtist) => {
    const formattedStart = dayjs(startDate as string).format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
    const formattedEnd = dayjs(endDate as string).format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)

    let artistIds = artists.length
      ? artists
          .map(a => a?.artistId ?? null)
          .sort()
          .filter(id => id !== null)
          .join(',')
      : ''

    if (selectedArtist !== null) artistIds = selectedArtist
    const newParams: EventsMonthParams = { startDate: formattedStart, endDate: formattedEnd, artistIds }

    // Check if the new params are different from the last params
    const lastParams = get().eventsMonthParams
    if (
      lastParams &&
      newParams.startDate === lastParams.startDate &&
      newParams.endDate === lastParams.endDate &&
      newParams.artistIds === lastParams.artistIds
    ) {
      return
    }

    // Update state and start fetching events
    set({ loading: true, errors: null, eventsMonthParams: newParams })

    try {
      const eventService = new EventService()
      const { events, activeMonthList, eventsPagination, activeArtistList } =
        await eventService.getEventsFromEventEngine(formattedStart, formattedEnd, artistIds)
      if (events.length == 0) {
        set({
          loading: false
        })
        return
      }
      const { eventsByDate, eventsByMonth, formattedEvents } = eventFormatter.processEventEngineResponse(events)

      if (selectedArtist !== null && activeMonthList && activeMonthList.length > 0) {
        const monthToSelect = get().isNavigationClicked
          ? activeMonthList.find(item => item.month === get().selectedMonth?.shortLabel)
          : activeMonthList[0]

        if (monthToSelect?.month) {
          set({ selectedMonth: formatMonth(dayjs(monthToSelect.month, DATE_FORMAT.MONTH_YEAR_SHORT)) })
        }
      } else if (activeMonthList && activeMonthList.length > 0) {
        const monthKeys = eventsByMonth ? Object.keys(eventsByMonth) : []
        const monthToSelect = monthKeys.length > 0 ? monthKeys[0] : null
        if (monthToSelect) {
          set({ selectedMonth: formatMonth(dayjs(monthToSelect, 'YYYY-MM')) })
        }
      }

      set({
        events,
        activeMonthList: activeMonthList || [],
        activeArtistList: selectedArtist == null ? activeArtistList : get().activeArtistList,
        eventsByDate,
        eventsByMonth,
        formattedEvents,
        eventsPagination,
        loading: false
      })
    } catch (error) {
      console.error(error)
      set({ errors: 'Failed to fetch events. Please try again later.', loading: false })
    }
  },
  fetchEventsForSingleArtist: async (startDate, endDate, artists) => {
    // Format dates for the API request
    const formattedStart = dayjs(startDate as string).format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)
    const formattedEnd = dayjs(endDate as string).format(DATE_FORMAT.SHORT_DATE_NO_SEPRATOR)

    const artistIds = artists.map(a => a?.artistId ?? null).join(',')

    const newParams: EventsMonthParams = { startDate: formattedStart, endDate: formattedEnd, artistIds }

    // Check if the new params are different from the last params
    const lastParams = get().eventsMonthParamsForSingleArtist
    if (
      lastParams &&
      newParams.startDate === lastParams.startDate &&
      newParams.endDate === lastParams.endDate &&
      newParams.artistIds === lastParams.artistIds
    ) {
      return
    }

    // Update state and start fetching events
    set({ loading: true, errors: null, eventsMonthParamsForSingleArtist: newParams })

    try {
      const eventService = new EventService()
      const { events, activeMonthList, eventsPagination } = await eventService.getEventsFromEventEngine(
        formattedStart,
        formattedEnd,
        artistIds
      )
      if (events.length == 0) {
        set({
          loading: false
        })
        return
      }
      const { eventsByDate, eventsByMonth, formattedEvents } = eventFormatter.processEventEngineResponse(events)

      set({
        eventsForSingleArtist: events,
        activeMonthListForSingleArtist: activeMonthList || [],
        eventsByDateForSingleArtist: eventsByDate,
        eventsByMonthForSingleArtist: eventsByMonth,
        formattedEvents,
        eventsPagination,
        loading: false
      })
    } catch (error) {
      console.error(error)
      set({ errors: 'Failed to fetch events. Please try again later.', loading: false })
    }
  }
}))
